import Big from 'big.js'
import { Measure } from '../types'

enum PowerMeasurementSystem {
  siUnits = 'SI Units',
  derivedUnits = 'SI Derived Units',
  btuUnits = 'British Thermal Unit Based Units',
  caloricUnits = 'Caloric Energy Units',
  mechanicalUnits = 'Mechanical Power Units',
  horsepowerVariants = 'Horsepower Variants Units',
  specialUnits = 'Special Units'
}


const powerUnits = {
  watt: {
    displayName: 'Watt',
    codeName: 'watt',
    url: 'watt',
    symbol: 'W',
    toBaseUnit: (value: Big): Big => value,
    fromBaseUnit: (value: Big): Big => value,
    system: PowerMeasurementSystem.siUnits
  },
  kilowatt: {
    displayName: 'Kilowatt',
    codeName: 'kilowatt',
    url: 'kilowatt',
    symbol: 'kW',
    toBaseUnit: (value: Big): Big => value.times(1000),
    fromBaseUnit: (value: Big): Big => value.div(1000),
    system: PowerMeasurementSystem.siUnits
  },
  megawatt: {
    displayName: 'Megawatt',
    codeName: 'megawatt',
    url: 'megawatt',
    symbol: 'MW',
    toBaseUnit: (value: Big): Big => value.times(1e6),
    fromBaseUnit: (value: Big): Big => value.div(1e6),
    system: PowerMeasurementSystem.siUnits
  },
  gigawatt: {
    displayName: 'Gigawatt',
    codeName: 'gigawatt',
    url: 'gigawatt',
    symbol: 'GW',
    toBaseUnit: (value: Big): Big => value.times(1e9),
    fromBaseUnit: (value: Big): Big => value.div(1e9),
    system: PowerMeasurementSystem.siUnits
  },
  terawatt: {
    displayName: 'Terawatt',
    codeName: 'terawatt',
    url: 'terawatt',
    symbol: 'TW',
    toBaseUnit: (value: Big): Big => value.times(1e12),
    fromBaseUnit: (value: Big): Big => value.div(1e12),
    system: PowerMeasurementSystem.siUnits
  },
  petawatt: {
    displayName: 'Petawatt',
    codeName: 'petawatt',
    url: 'petawatt',
    symbol: 'PW',
    toBaseUnit: (value: Big): Big => value.times(1e15),
    fromBaseUnit: (value: Big): Big => value.div(1e15),
    system: PowerMeasurementSystem.siUnits
  },

  joules_second: {
    displayName: 'Joules/second',
    codeName: 'joules_second',
    url: 'joules-second',
    symbol: 'J/s',
    toBaseUnit: (value: Big): Big => value,
    fromBaseUnit: (value: Big): Big => value,
    system: PowerMeasurementSystem.derivedUnits
  },
  joules_minute: {
    displayName: 'Joules/minute',
    codeName: 'joules_minute',
    url: 'joules-minute',
    symbol: 'J/min',
    toBaseUnit: (value: Big): Big => value.times(0.0166666666666667),
    fromBaseUnit: (value: Big): Big => value.div(0.0166666666666667),
    system: PowerMeasurementSystem.derivedUnits
  },
  joules_hour: {
    displayName: 'Joules/hour',
    codeName: 'joules_hour',
    url: 'joules-hour',
    symbol: 'J/h',
    toBaseUnit: (value: Big): Big => value.times(0.000277777777777778),
    fromBaseUnit: (value: Big): Big => value.div(0.000277777777777778),
    system: PowerMeasurementSystem.derivedUnits
  },


  btu_second: {
    displayName: 'BTU/second',
    codeName: 'btu_second',
    url: 'btu-second',
    symbol: 'BTU/s',
    toBaseUnit: (value: Big): Big => value.times(1055.05585262002),
    fromBaseUnit: (value: Big): Big => value.div(1055.05585262002),
    system: PowerMeasurementSystem.btuUnits
  },
  btu_minute: {
    displayName: 'BTU/minute',
    codeName: 'btu_minute',
    url: 'btu-minute',
    symbol: 'BTU/min',
    toBaseUnit: (value: Big): Big => value.times(17.5842642103337),
    fromBaseUnit: (value: Big): Big => value.div(17.5842642103337),
    system: PowerMeasurementSystem.btuUnits
  },
  btu_hour: {
    displayName: 'BTU/hour',
    codeName: 'btu_hour',
    url: 'btu-hour',
    symbol: 'BTU/h',
    toBaseUnit: (value: Big): Big => value.times(0.293071070172228),
    fromBaseUnit: (value: Big): Big => value.div(0.293071070172228),
    system: PowerMeasurementSystem.btuUnits
  },
  calories_second: {
    displayName: 'Calories (th)/second',
    codeName: 'calories_second',
    url: 'calories-th-second',
    symbol: 'cal(th)/s',
    toBaseUnit: (value: Big): Big => value.times(4.184),
    fromBaseUnit: (value: Big): Big => value.div(4.184),
    system: PowerMeasurementSystem.caloricUnits
  },


  calories_minute: {
    displayName: 'Calories (th)/minute',
    codeName: 'calories_minute',
    url: 'calories-th-minute',
    symbol: 'cal(th)/min',
    toBaseUnit: (value: Big): Big => value.times(0.0697333333333238),
    fromBaseUnit: (value: Big): Big => value.div(0.0697333333333238),
    system: PowerMeasurementSystem.caloricUnits
  },
  calories_hour: {
    displayName: 'Calories (th)/hour',
    codeName: 'calories_hour',
    url: 'calories-th-hour',
    symbol: 'cal(th)/h',
    toBaseUnit: (value: Big): Big => value.times(0.00116222222222206),
    fromBaseUnit: (value: Big): Big => value.div(0.00116222222222206),
    system: PowerMeasurementSystem.caloricUnits
  },
  kilocalories_minute: {
    displayName: 'Kilocalories (th)/minute',
    codeName: 'kilocalories_minute',
    url: 'kilocalories-th-minute',
    symbol: 'kcal/min',
    toBaseUnit: (value: Big): Big => value.times(69.7333333333238),
    fromBaseUnit: (value: Big): Big => value.div(69.7333333333238),
    system: PowerMeasurementSystem.caloricUnits
  },
  kilocalories_hour: {
    displayName: 'Kilocalories (th)/hour',
    codeName: 'kilocalories_hour',
    url: 'kilocalories-th-hour',
    symbol: 'kcal/h',
    toBaseUnit: (value: Big): Big => value.times(1.16222222222206),
    fromBaseUnit: (value: Big): Big => value.div(1.16222222222206),
    system: PowerMeasurementSystem.caloricUnits
  },


  foot_pounds_force_second: {
    displayName: 'Foot pounds-force second',
    codeName: 'foot_pounds_force_second',
    url: 'foot-pounds-force-second',
    symbol: 'ft·lbf/s',
    toBaseUnit: (value: Big): Big => value.times(1.35581794833508),
    fromBaseUnit: (value: Big): Big => value.div(1.35581794833508),
    system: PowerMeasurementSystem.mechanicalUnits
  },
  foot_pounds_force_minute: {
    displayName: 'Foot pounds-force minute',
    codeName: 'foot_pounds_force_minute',
    url: 'foot-pounds-force-minute',
    symbol: 'ft·lbf/min',
    toBaseUnit: (value: Big): Big => value.times(0.0225969658055847),
    fromBaseUnit: (value: Big): Big => value.div(0.0225969658055847),
    system: PowerMeasurementSystem.mechanicalUnits
  },
  kilogram_force_meters_minute: {
    displayName: 'Kilogram-force meters/minute',
    codeName: 'kilogram_force_meters_minute',
    url: 'kilogram-force-meters-minute',
    symbol: 'kgf·m/min',
    toBaseUnit: (value: Big): Big => value.times(0.16344416666667),
    fromBaseUnit: (value: Big): Big => value.div(0.16344416666667),
    system: PowerMeasurementSystem.mechanicalUnits
  },
  kilogram_force_meters_hour: {
    displayName: 'Kilogram-force meters/hour',
    codeName: 'kilogram_force_meters_hour',
    url: 'kilogram-force-meters-hour',
    symbol: 'kgf·m/h',
    toBaseUnit: (value: Big): Big => value.times(0.0027240694444444755),
    fromBaseUnit: (value: Big): Big => value.div(0.0027240694444444755),
    system: PowerMeasurementSystem.mechanicalUnits
  },


  horsepower_metric: {
    displayName: 'Horsepower (Metric)',
    codeName: 'horsepower_metric',
    url: 'horsepower-metric',
    symbol: 'hp',
    toBaseUnit: (value: Big): Big => value.times(735.49875),
    fromBaseUnit: (value: Big): Big => value.div(735.49875),
    system: PowerMeasurementSystem.horsepowerVariants
  },
  horsepower_british: {
    displayName: 'Horsepower (British)',
    codeName: 'horsepower_british',
    url: 'horsepower-british',
    symbol: 'hp',
    toBaseUnit: (value: Big): Big => value.times(745.699872),
    fromBaseUnit: (value: Big): Big => value.div(745.699872),
    system: PowerMeasurementSystem.horsepowerVariants
  },
  horsepower_electric: {
    displayName: 'Horsepower (Electric)',
    codeName: 'horsepower_electric',
    url: 'horsepower-electric',
    symbol: 'hp',
    toBaseUnit: (value: Big): Big => value.times(746),
    fromBaseUnit: (value: Big): Big => value.div(746),
    system: PowerMeasurementSystem.horsepowerVariants
  },
  horsepower_international: {
    displayName: 'Horsepower (International)',
    codeName: 'horsepower_international',
    url: 'horsepower-international',
    symbol: 'hp',
    toBaseUnit: (value: Big): Big => value.times(745.699872),
    fromBaseUnit: (value: Big): Big => value.div(745.699872),
    system: PowerMeasurementSystem.horsepowerVariants
  },
  horsepower_water: {
    displayName: 'Horsepower (Water)',
    codeName: 'horsepower_water',
    url: 'horsepower-water',
    symbol: 'hp',
    toBaseUnit: (value: Big): Big => value.times(746.043),
    fromBaseUnit: (value: Big): Big => value.div(746.043),
    system: PowerMeasurementSystem.horsepowerVariants
  },


  ton_of_refrigeration: {
    displayName: 'Ton of refrigeration',
    codeName: 'ton_of_refrigeration',
    url: 'ton-of-refrigeration',
    symbol: 'TR',
    toBaseUnit: (value: Big): Big => value.times(3516.85284206661),
    fromBaseUnit: (value: Big): Big => value.div(3516.85284206661),
    system: PowerMeasurementSystem.specialUnits
  },
  donkeypower: {
    displayName: 'Donkeypower',
    codeName: 'donkeypower',
    url: 'donkeypower',
    symbol: 'dp',
    toBaseUnit: (value: Big): Big => value.times(250),
    fromBaseUnit: (value: Big): Big => value.div(250),
    system: PowerMeasurementSystem.specialUnits
  }
}

export const power: Measure = {
  displayName: 'Power',
  codeName: 'power',
  url: 'power',
  units: powerUnits,
  baseUnit: powerUnits.watt,
  defaultFromUnit: powerUnits.kilowatt,
  defaultToUnit: powerUnits.watt
}
